// ETF reference data for the DSE-listed exchange-traded funds.
//
// PROVENANCE — every number here is transcribed from a committed source, not estimated.
// The basket, sleeve, fee and anchor come from dse-compute/internal/inav/calibrated.go
// (VertexBasket, VertexAnchor); the band from history.go (BandBps); the NAV series from
// dse-compute/internal/inav/published_nav.json, which that repo in turn sourced from
// dse-data-lab research/etf_mm/data/published_nav.csv (scraped from vertex.co.tz by
// scrape_nav.py). Nothing on this page is invented.
//
// WHY IT IS BUNDLED RATHER THAN FETCHED: the brain emits nothing ETF-related — neither ETF
// is in config/symbols.yaml, neither has a DSE sec_id, and there is no ETF message kind on
// the WebSocket. The published series is 14.5 KB and static, so shipping it as a build-time
// asset is honest and costs one import. When an ETF feed exists this file becomes the
// fallback, not the source.

import published from '../data/vertexPublishedNav.json';
import constituentData from '../data/vertexConstituentCloses.json';

/** A constituent of an ETF's replicating basket. */
export interface EtfConstituent {
  /** DSE ticker. */
  symbol: string;
  /** Fraction of the EQUITY sleeve, not of the whole fund. Sums to 1 across constituents. */
  weight: number;
}

export interface EtfBasket {
  symbol: string;
  constituents: EtfConstituent[];
  /** Fraction of fund value held as equities; the remainder is cash. */
  equityShare: number;
  /** All-in annual fee (management + trustee), accrued daily off NAV. */
  feePA: number;
  /** Assumed annual yield on the cash sleeve. */
  cashYieldPA: number;
  /** The instrument's own daily price band — also the reflexivity guard on any iNAV. */
  maxIntradayDrift: number;
}

/**
 * VERTEX-ETF's replicating basket.
 *
 * dse-compute/internal/inav/calibrated.go:33-47. Equal 20% weights within an 80% equity
 * sleeve; beta(21d) = 0.804 on the clean sample against 0.518 for equal share count.
 * Fees are 1.20% management + 0.10% trustee. The 5% drift cap is the instrument's own
 * daily price band, cut from 15% on 2025-11-24.
 */
export const VERTEX_BASKET: EtfBasket = {
  symbol: 'VERTEX-ETF',
  constituents: [
    { symbol: 'CRDB', weight: 0.2 },
    { symbol: 'NMB', weight: 0.2 },
    { symbol: 'NICO', weight: 0.2 },
    { symbol: 'DSE', weight: 0.2 },
    { symbol: 'AFRIPRISE', weight: 0.2 },
  ],
  equityShare: 0.8,
  feePA: 0.013,
  cashYieldPA: 0.08,
  maxIntradayDrift: 0.05,
};

/**
 * The last PUBLISHED NAV paired with the official closes it was struck on.
 *
 * dse-compute/internal/inav/calibrated.go:66-79. The pairing is load-bearing: a NAV from
 * one session with closes from another double-counts that session's move — an error worth
 * roughly 3x the iNAV's own accuracy. Both are the 2026-07-30 row.
 */
export const VERTEX_ANCHOR = {
  date: '2026-07-30',
  nav: 362.1284,
  closes: { CRDB: 2630, NMB: 17430, NICO: 3570, DSE: 6290, AFRIPRISE: 640 } as Record<string, number>,
};

/** Prospectus AP creation/redemption band, ±5% (history.go:73 `BandBps = 500.0`). */
export const AP_BAND_BPS = 500;

/**
 * §4.2.2 and §12.5 are TWO DIFFERENT TESTS, not one number stated twice.
 *
 * §4.2.2 is a point-in-time obligation at ±5%. §12.5 is a 0.5–1% trigger measured over a
 * 30-day rolling period. A wide instantaneous band alongside a tight smoothed one is a
 * coherent design, so the page evaluates both and labels each by clause rather than
 * presenting the pair as a contradiction.
 */
export const AP_BAND_CAVEAT =
  'VIS-ETF Offer Document §4.2.2: APs are REQUIRED to create when price exceeds NAV by 5% and redeem when it falls 5% below. §12.5 adds a separate 0.5–1% trigger measured over a 30-day rolling period.';

export interface NavSession {
  date: string;
  /** Published NAV per unit, TZS. */
  nav: number;
  /** The ETF's own official close that session, TZS. */
  close: number;
  /** close/nav - 1, in basis points. Positive = premium, negative = discount. */
  premiumBps: number;
}

/**
 * The published NAV series with per-session premium/discount derived.
 *
 * A malformed row is SKIPPED rather than defaulted to zero — mirroring history.go:103-171.
 * A 0bps premium is a perfectly plausible reading, so defaulting would inject a fake
 * data point rather than an obvious hole.
 */
export const VERTEX_NAV_HISTORY: NavSession[] = (published.sessions as Array<{ date: string; nav: string; close: string }>)
  .map(s => {
    const nav = Number(s.nav);
    const close = Number(s.close);
    if (!Number.isFinite(nav) || nav <= 0 || !Number.isFinite(close)) return null;
    return { date: s.date, nav, close, premiumBps: (close / nav - 1) * 10_000 };
  })
  .filter((s): s is NavSession => s !== null);

export const VERTEX_NAV_PROVENANCE: string = published.provenance;

/** Regime lookback — long enough to be a regime, short enough that a sign flip shows up. */
const TRAILING_WINDOW = 40;

export interface NavRegime {
  sessions: number;
  insideBandPct: number;
  minBps: number;
  maxBps: number;
  latestBps: number;
  trailingMeanBps: number;
  /** Longest unbroken run of sessions outside the AP band. */
  longestOutsideRun: number;
  firstDate: string;
  lastDate: string;
}

export function deriveNavRegime(series: NavSession[] = VERTEX_NAV_HISTORY): NavRegime | null {
  if (series.length === 0) return null;
  const bps = series.map(s => s.premiumBps);
  const inside = bps.filter(b => Math.abs(b) <= AP_BAND_BPS).length;
  const tail = bps.slice(-TRAILING_WINDOW);

  let run = 0;
  let longest = 0;
  for (const b of bps) {
    if (Math.abs(b) > AP_BAND_BPS) { run += 1; longest = Math.max(longest, run); }
    else run = 0;
  }

  return {
    sessions: series.length,
    insideBandPct: (inside / series.length) * 100,
    minBps: Math.min(...bps),
    maxBps: Math.max(...bps),
    latestBps: bps[bps.length - 1],
    trailingMeanBps: tail.reduce((a, b) => a + b, 0) / tail.length,
    longestOutsideRun: longest,
    firstDate: series[0].date,
    lastDate: series[series.length - 1].date,
  };
}

/** Sessions between the anchor date and now — the staleness the operator must see. */
export function staleSessionsSince(dateIso: string, now: Date = new Date()): number {
  const then = new Date(`${dateIso}T00:00:00Z`).getTime();
  if (!Number.isFinite(then)) return 0;
  let sessions = 0;
  for (let t = then + 86_400_000; t <= now.getTime(); t += 86_400_000) {
    const day = new Date(t).getUTCDay();
    if (day !== 0 && day !== 6) sessions += 1;
  }
  return sessions;
}

/**
 * IEACLC-ETF is listed and actively traded (23,110 rows on 2026-07-31 in the dse-scraper
 * capture, median 1,340 TZS) but we hold NO basket, NO weights and NO NAV series for it.
 *
 * It is listed here deliberately so the page can render it as an explicit gap. A guessed
 * weight does not announce itself — it quietly biases the premium/discount a desk trades
 * on, which is the exact error an ETF desk exists to prevent.
 */
export const ETF_SYMBOLS = ['VERTEX-ETF', 'IEACLC-ETF'] as const;
export type EtfSymbol = (typeof ETF_SYMBOLS)[number];

// ─────────────────────────────────────────────────────────────────────────────
// AUTHORIZED PARTICIPANT PARAMETERS
//
// Every constant below is a clause reference into the VIS-ETF Offer Document
// (August 2025). Where the prospectus is silent, the value is explicitly null and
// the UI renders an em-dash rather than a default — a guessed creation-unit size
// would silently rescale every basket figure on the page.
// ─────────────────────────────────────────────────────────────────────────────

/** §5.6 — face value at IPO. */
export const FACE_VALUE_TZS = 200;
/** §5.6 — 25,000,000 units offered at TZS 200, targeting TZS 5bn. */
export const IPO_UNITS = 25_000_000;
export const IPO_TARGET_TZS = 5_000_000_000;
/** §5.6 — an IPO RETAIL minimum. NOT the creation unit size. */
export const MIN_SUBSCRIPTION_UNITS = 100;
/** §8 — all DSE transactions carry 18% VAT (levied on the commission, not the notional). */
export const VAT_RATE = 0.18;
/** §8 — 5% withholding on cash distributions; no capital gains tax on listed securities. */
export const WITHHOLDING_ON_DISTRIBUTIONS = 0.05;
export const CAPITAL_GAINS_RATE_LISTED = 0;
/** §12.5 — equal weights are reset quarterly. */
export const REBALANCE_FREQUENCY = 'quarterly' as const;

/**
 * The creation unit size is NOT STATED anywhere in the Offer Document.
 *
 * §5.6 gives a 100-unit minimum SUBSCRIPTION, which is a retail IPO floor and a different
 * thing entirely. Every basket quantity scales linearly in this number, so it is an operator
 * input, not a constant. Confirm with Vertex International Securities Ltd.
 */
export const CREATION_UNIT = {
  units: null as number | null,
  source: 'Not stated in the VIS-ETF Offer Document — confirm with Vertex International Securities Ltd.',
};

/** §12.5 — the rolling test's window and its two thresholds. */
export const S125_ROLLING_SESSIONS = 30;
export const S125_WARN_BPS = 50;
export const S125_ACT_BPS = 100;

export type ApAction = 'CREATE' | 'REDEEM' | 'HOLD';

/**
 * §4.2.2 — the point-in-time test. "Whenever ETF share price deviate by 5 percent less or more
 * than the NAV, Authorized participant will be required to redeem units in case the price is
 * less and create units in case the price is more."
 *
 * Note the direction: a PREMIUM (price above NAV) obliges CREATION — buy the basket, deliver it,
 * receive units, sell them into the premium. A DISCOUNT obliges REDEMPTION.
 */
export function s422Action(premiumBps: number): ApAction {
  if (premiumBps > AP_BAND_BPS) return 'CREATE';
  if (premiumBps < -AP_BAND_BPS) return 'REDEEM';
  return 'HOLD';
}

/**
 * §12.5 — the rolling test, read as a mean over the trailing 30 published sessions.
 *
 * This reading is an INTERPRETATION and the page says so: the clause's wording ("deviates by
 * 0.5% to 1% over a 30-day rolling period") does not specify mean, max, or persistence. Under
 * the mean reading the test is satisfied on 99.3% of eligible sessions, which makes it a
 * near-constant signal rather than a tighter version of §4.2.2.
 */
export function s125Rolling(series: NavSession[] = VERTEX_NAV_HISTORY): { meanBps: number; action: ApAction; eligible: boolean } {
  if (series.length < S125_ROLLING_SESSIONS) return { meanBps: NaN, action: 'HOLD', eligible: false };
  const tail = series.slice(-S125_ROLLING_SESSIONS);
  const meanBps = tail.reduce((a, s) => a + s.premiumBps, 0) / tail.length;
  const action: ApAction = Math.abs(meanBps) < S125_ACT_BPS ? 'HOLD' : meanBps > 0 ? 'CREATE' : 'REDEEM';
  return { meanBps, action, eligible: true };
}

/** How many sessions the current §4.2.2 verdict has held unbroken. */
export function currentRun(series: NavSession[] = VERTEX_NAV_HISTORY): { action: ApAction; sessions: number } {
  if (series.length === 0) return { action: 'HOLD', sessions: 0 };
  const action = s422Action(series[series.length - 1].premiumBps);
  let n = 0;
  for (let i = series.length - 1; i >= 0; i -= 1) {
    if (s422Action(series[i].premiumBps) !== action) break;
    n += 1;
  }
  return { action, sessions: n };
}

/** Distribution of the §4.2.2 verdict across the whole published history. */
export function actionHistogram(series: NavSession[] = VERTEX_NAV_HISTORY): Record<ApAction, number> {
  const h: Record<ApAction, number> = { CREATE: 0, REDEEM: 0, HOLD: 0 };
  for (const s of series) h[s422Action(s.premiumBps)] += 1;
  return h;
}

export interface BasketLeg {
  symbol: string;
  weight: number;
  /** Official close the basket is struck on. */
  close: number;
  /** Shares of this constituent backing ONE ETF unit. */
  sharesPerUnit: number;
}

/**
 * Decompose one ETF unit into its constituent shares.
 *
 * equity_value_per_unit = NAV × equityShare          (the 80% sleeve, §6.4)
 * value_per_leg         = equity_value_per_unit × wᵢ (equal 20% each, §6.6)
 * shares_per_unitᵢ      = value_per_leg / closeᵢ
 *
 * The remaining (1 − equityShare) × NAV is the cash component, delivered or received as cash.
 */
export function basketPerUnit(
  nav: number = VERTEX_ANCHOR.nav,
  closes: Record<string, number> = VERTEX_ANCHOR.closes,
  basket: EtfBasket = VERTEX_BASKET,
): { legs: BasketLeg[]; equityPerUnit: number; cashPerUnit: number } {
  const equityPerUnit = nav * basket.equityShare;
  const legs = basket.constituents.map(c => {
    const close = closes[c.symbol];
    const valuePerLeg = equityPerUnit * c.weight;
    return {
      symbol: c.symbol,
      weight: c.weight,
      close,
      sharesPerUnit: close > 0 ? valuePerLeg / close : NaN,
    };
  });
  return { legs, equityPerUnit, cashPerUnit: nav * (1 - basket.equityShare) };
}

/**
 * What the fund's own basket did versus what the fund did.
 *
 * From our research: an equal-weight buy-and-hold of the five constituents returned +87% over
 * the published window, daily rebalancing +85%, and the stated 80/20 structure implies +71%.
 * The fund's actual NAV returned +43%. The ~28pp residual is NOT explained by the 1.30% fee,
 * an 8% cash yield, or the 80/20 split.
 *
 * For an AP this is the load-bearing number on the page: creation and redemption are struck at
 * NAV, so if NAV does not track the basket, the arb is aimed at a moving target.
 */
export const TRACKING_STUDY = {
  basketBuyHoldPct: 87,
  basketDailyRebalPct: 85,
  implied8020Pct: 71,
  actualNavPct: 43,
  windowStart: '2025-11-06',
  windowEnd: '2026-07-30',
  unexplainedPp: 71 - 43,
};

// ─────────────────────────────────────────────────────────────────────────────
// FUND SIZE, DERIVED HOLDINGS, AND REBALANCE
// ─────────────────────────────────────────────────────────────────────────────


export interface FundSession extends NavSession {
  /** Total fund net value, TZS. Published by Vertex. */
  fundValue: number;
  /** Units in issue. Published by Vertex. */
  units: number;
  /** Units created that session. Published by Vertex. */
  addedUnits: number;
}

const RAW = published.sessions as Array<Record<string, string>>;

/** The published series with fund size attached. */
export const VERTEX_FUND_HISTORY: FundSession[] = RAW.map(r => {
  const nav = Number(r.nav), close = Number(r.close);
  if (!Number.isFinite(nav) || nav <= 0 || !Number.isFinite(close)) return null;
  return {
    date: r.date, nav, close, premiumBps: (close / nav - 1) * 10_000,
    fundValue: Number(r.fundValue), units: Number(r.units), addedUnits: Number(r.addedUnits),
  };
}).filter((x): x is FundSession => x !== null);

export const LATEST_FUND = VERTEX_FUND_HISTORY[VERTEX_FUND_HISTORY.length - 1] ?? null;

/** Official closes for the five constituents, from the dse-scraper session archive. */
export const CONSTITUENT_CLOSES: Record<string, Record<string, number>> = constituentData.closes;
export const CONSTITUENT_PROVENANCE: string = constituentData.provenance;

export interface Holding extends BasketLeg {
  /** Shares the fund holds in this name, DERIVED — Vertex publishes no holdings table. */
  totalShares: number;
  /** Market value of that holding at `close`, TZS. */
  marketValue: number;
}

/**
 * The fund's constituent holdings.
 *
 * DERIVED, NOT DISCLOSED. vertex.co.tz publishes NAV, units in issue, fund net value and the
 * closing price — but no holdings table, no share counts and no per-name weights. So:
 *
 *   equity sleeve   = fundValue × equityShare        (§6.4, 80%)
 *   value per leg   = equity sleeve × wᵢ             (§6.6, equal 20%)
 *   shares held     = value per leg / closeᵢ
 *
 * That is exact IF the fund is holding its stated weights. It will diverge between quarterly
 * rebalances (§12.5) as prices drift, which is precisely what the rebalance panel measures.
 */
export function deriveHoldings(
  fundValue: number = LATEST_FUND?.fundValue ?? 0,
  closes: Record<string, number> = VERTEX_ANCHOR.closes,
  basket: EtfBasket = VERTEX_BASKET,
): Holding[] {
  const sleeve = fundValue * basket.equityShare;
  return basket.constituents.map(c => {
    const close = closes[c.symbol];
    const value = sleeve * c.weight;
    const shares = close > 0 ? value / close : NaN;
    return {
      symbol: c.symbol, weight: c.weight, close,
      sharesPerUnit: (LATEST_FUND?.units ?? 0) > 0 ? shares / (LATEST_FUND as FundSession).units : NaN,
      totalShares: shares,
      marketValue: value,
    };
  });
}

export interface RebalanceLeg {
  symbol: string;
  close: number;
  /** Current weight within the equity sleeve, if holdings were last set at `since`. */
  actualWeight: number;
  targetWeight: number;
  driftBps: number;
  /** Positive = BUY this many shares to restore target; negative = SELL. */
  shareDelta: number;
  notional: number;
}

/**
 * What it would take to restore equal weights (§12.5, quarterly).
 *
 * Holdings are assumed set at `sinceDate` closes and marked at `nowDate` closes; drift is then
 * pure price action. If the fund rebalanced more recently than `sinceDate` this OVERSTATES the
 * drift — the panel says so rather than implying we can see their trade log.
 */
export function deriveRebalance(sinceDate: string, nowDate: string, basket: EtfBasket = VERTEX_BASKET): RebalanceLeg[] | null {
  const then = CONSTITUENT_CLOSES[sinceDate];
  const now = CONSTITUENT_CLOSES[nowDate];
  const fund = LATEST_FUND;
  if (!then || !now || !fund) return null;

  const sleeve = fund.fundValue * basket.equityShare;
  // Shares implied at the earlier date, then marked at the later one.
  const held = basket.constituents.map(c => ({ symbol: c.symbol, target: c.weight, shares: (sleeve * c.weight) / then[c.symbol] }));
  const marked = held.map(h => ({ ...h, value: h.shares * now[h.symbol] }));
  const total = marked.reduce((a, m) => a + m.value, 0);
  if (!(total > 0)) return null;

  return marked.map(m => {
    const actualWeight = m.value / total;
    const targetValue = total * m.target;
    const shareDelta = (targetValue - m.value) / now[m.symbol];
    return {
      symbol: m.symbol,
      close: now[m.symbol],
      actualWeight,
      targetWeight: m.target,
      driftBps: (actualWeight - m.target) * 10_000,
      shareDelta,
      notional: targetValue - m.value,
    };
  });
}

/** Cumulative-return comparison: the fund against its own basket, all rebased to the first session. */
export function deriveReturnSeries(): Array<{ date: string; navPct: number; buyHoldPct: number; rebalPct: number; implied8020Pct: number }> {
  const dates = Object.keys(CONSTITUENT_CLOSES).sort();
  const navByDate = new Map(VERTEX_FUND_HISTORY.map(s => [s.date, s.nav]));
  const usable = dates.filter(d => navByDate.has(d));
  if (usable.length < 2) return [];

  const first = CONSTITUENT_CLOSES[usable[0]];
  const nav0 = navByDate.get(usable[0])!;
  const w = 1 / VERTEX_BASKET.constituents.length;
  // Buy & hold: fixed share counts bought at t0. Rebalanced: weights reset every session.
  const shares0 = Object.fromEntries(VERTEX_BASKET.constituents.map(c => [c.symbol, w / first[c.symbol]]));
  let rebalIdx = 1;

  return usable.map((d, i) => {
    const px = CONSTITUENT_CLOSES[d];
    const buyHold = VERTEX_BASKET.constituents.reduce((a, c) => a + shares0[c.symbol] * px[c.symbol], 0);
    if (i > 0) {
      const prev = CONSTITUENT_CLOSES[usable[i - 1]];
      const step = VERTEX_BASKET.constituents.reduce((a, c) => a + w * (px[c.symbol] / prev[c.symbol]), 0);
      rebalIdx *= step;
    }
    const equityPct = (buyHold - 1) * 100;
    return {
      date: d.slice(5),
      navPct: (navByDate.get(d)! / nav0 - 1) * 100,
      buyHoldPct: equityPct,
      rebalPct: (rebalIdx - 1) * 100,
      // The stated structure: 80% in the basket, 20% cash earning the assumed yield, less fees.
      implied8020Pct: equityPct * VERTEX_BASKET.equityShare,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CAPACITY AND LOOK-THROUGH — the two numbers with no home anywhere else
// ─────────────────────────────────────────────────────────────────────────────

export interface CapacityLeg {
  symbol: string;
  /** Resting bid quantity in that constituent's book. */
  bidDepth: number;
  sharesPerUnit: number;
  /** How many ETF units this leg alone could support liquidating into. */
  unitsSupported: number;
  binding: boolean;
}

export interface EtfCapacity {
  legs: CapacityLeg[];
  /** Units the whole basket supports — the binding leg, not the average. */
  sellLegUnits: number | null;
  bindingSymbol: string | null;
}

/**
 * How many units the STANDING BOOK can actually absorb.
 *
 * A discount is only tradeable to the extent the books allow: the gap is not the
 * constraint, the depth is. Equal-VALUE weighting makes share counts wildly unequal, so a
 * cheap constituent binds the book long before an expensive one — which is exactly why this
 * reports the binding leg rather than a total.
 *
 * A leg with no shares-per-unit is SKIPPED rather than divided by zero, because an infinite
 * capacity would read as "unconstrained" — the most dangerous possible wrong answer here.
 */
export function deriveCapacity(
  legs: BasketLeg[],
  bidDepthBySymbol: Record<string, number>,
): EtfCapacity | null {
  const out: CapacityLeg[] = [];
  for (const leg of legs) {
    if (!(leg.sharesPerUnit > 0)) continue;
    const bidDepth = bidDepthBySymbol[leg.symbol] ?? 0;
    out.push({
      symbol: leg.symbol,
      bidDepth,
      sharesPerUnit: leg.sharesPerUnit,
      unitsSupported: bidDepth / leg.sharesPerUnit,
      binding: false,
    });
  }
  if (out.length === 0) return null;
  out.sort((a, b) => a.unitsSupported - b.unitsSupported);
  out[0].binding = true;
  return { legs: out, sellLegUnits: out[0].unitsSupported, bindingSymbol: out[0].symbol };
}

export interface LookThroughRow {
  symbol: string;
  /** Shares held directly in that name. */
  direct: number;
  /** Shares implied by the ETF units held. */
  viaEtf: number;
  combined: number;
  limit: number;
  utilizationPct: number;
  breached: boolean;
}

/**
 * Combined exposure — direct plus ETF-implied.
 *
 * This is the number with no home anywhere else: per-ticker limits cannot see ETF-implied
 * exposure, so a desk can sit inside every single-name check and still be over its true
 * position, silently. VERTEX overlaps four of the six counters we quote, so the effect is
 * not hypothetical for us.
 *
 * Utilisation is on ABSOLUTE exposure — a short consumes limit exactly as a long does, and
 * signing it would let a short read as comfortably unused.
 */
export function deriveLookThrough(
  legs: BasketLeg[],
  etfUnitsHeld: number,
  directBySymbol: Record<string, number>,
  limitBySymbol: Record<string, number>,
): LookThroughRow[] {
  return legs.map(leg => {
    const viaEtf = etfUnitsHeld * leg.sharesPerUnit;
    const direct = directBySymbol[leg.symbol] ?? 0;
    const limit = limitBySymbol[leg.symbol] ?? 0;
    const combined = direct + viaEtf;
    return {
      symbol: leg.symbol,
      direct,
      viaEtf,
      combined,
      limit,
      utilizationPct: limit > 0 ? (Math.abs(combined) / limit) * 100 : 0,
      breached: limit > 0 && Math.abs(combined) > limit,
    };
  }).sort((a, b) => a.symbol.localeCompare(b.symbol));
}

/**
 * Confidence band on the reference price, mirroring dse-compute's model: a 34bps base
 * (measured against 176 published NAVs) widened by roughly 24bps per stale session, because
 * an anchor struck N sessions ago has had N sessions of unobserved drift.
 */
export const BASE_CONFIDENCE_BPS = 34;
export const STALENESS_WIDENING_BPS = 24;

export function referenceConfidenceBps(staleSessions: number): number {
  return BASE_CONFIDENCE_BPS + STALENESS_WIDENING_BPS * Math.max(0, staleSessions);
}
